import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { ArticleCard } from "@/components/site/ArticleCard";
import { listBlogPostsLocalized } from "@/lib/blog-localized.functions";
import { ContactForm } from "@/components/site/ContactForm";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { EditorialHero } from "@/components/site/wow/EditorialHero";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { aeoGeo } from "@/lib/seo/schema";

export const Route = createFileRoute("/en_/knowledge")({
  head: () => ({
    meta: [
      { title: "Knowledge — Fenny's blog on video content" },
      {
        name: "description",
        content:
          "Strategy, formats and insights for marketing teams working with video. Written by Fenny — premium video production as a subscription.",
      },
      { property: "og:title", content: "Knowledge — Fenny's blog" },
      {
        property: "og:description",
        content:
          "Strategy, formats and insights for marketing teams working with video.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/en/knowledge" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/en/knowledge" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/knowledge", name: "Knowledge — Fenny's blog on video content", description: "Strategy, formats and insights for marketing teams working with video. Written by Fenny — premium video production as a subscription."})) },
    ],
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["blog-posts", "en"],
      queryFn: () => listBlogPostsLocalized({ data: { locale: "en" } }),
    }),
  component: KnowledgePage,
});

function KnowledgePage() {
  const fetchPosts = useServerFn(listBlogPostsLocalized);
  const { data } = useQuery({
    queryKey: ["blog-posts", "en"],
    queryFn: () => fetchPosts({ data: { locale: "en" } }),
  });
  const posts = data?.posts ?? [];
  const [featured, ...rest] = posts;

  return (
    <div>
      <EditorialHero
        eyebrow="Knowledge"
        title="We write about what we do."
        highlight={[3, 4, 5]}
        tone="mint"
        lede="Strategy, creative direction, formats, distribution and everything in between. What we learn from every production, gathered in one place — no buzzwords."
      />

      {featured && (
        <Section className="pt-4">
          <ArticleCard post={featured} featured />
        </Section>
      )}

      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Latest"
          title="More from the library"
          lede="Short, concrete articles based on real productions and campaigns."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => (
            <ArticleCard key={p.slug} post={p} />
          ))}
        </div>
        {posts.length === 0 && (
          <p className="mt-12 text-center text-muted-foreground">
            No published articles yet.
          </p>
        )}
      </Section>

      <Section className="bg-surface/40">
        <div className="mx-auto max-w-4xl">
          <LeadMagnetForm id="formatguide" location="en_knowledge_lead_magnet" />
        </div>
      </Section>

      <SignatureCta
        eyebrow="From knowledge to action"
        title="This becomes films. We make them."
        body="Everything we write about is delivered every week for our Pulse clients. A 30-minute call is enough to see if it's a fit."
        tone="mint"
      />

      <ContactForm location="en_knowledge" />
    </div>
  );
}
